import { config } from '@uhg-abyss/web/tools/config';
import { useState } from 'react';
import { useShallow } from 'zustand/react/shallow';

import {
  CareCategories,
  ChipCategories,
  Constants,
  CoverageTypesCodes,
  OPTUM_CORRELATION_ID_HEADER,
  ReverseCoverageTypesCodes,
} from '../../common/Constants';
import { ConstantsLagoon } from '../../common/ConstantsLagoon';
import { getLanguage } from '../../frontends/ProviderSearch/context/Internationalization/helpers';
import {
  getBehavioralProgramId,
  getUpdatedAoeCodesWithEAP,
} from '../../frontends/ProviderSearch/routes/SearchResults/utils';
import { useCurrentMemberIsPreEffective } from '../../hooks/useCurrentMemberIsPreEffective';
import { useChipStore } from '../../store/useChipStore';
import { ChipState } from '../../store/useChipStore/chipStore';
import { supportNewCodes } from '../../utils/common';
import {
  getLob,
  getMemberHealthCoverageType,
} from '../../utils/providerSearch.utils';
import {
  getClaimOrEligibilitySystemTypeCode,
  getCoverageEffDateRange,
  getCurrentMember,
  getMemberPlanYear,
  getPlanVariationCode,
  getRulesPackageKey,
} from '../../utils/user.utils';
import { useCategoriesCountV2 } from '../useCategoriesCountV2';
import { getCategoryLLMFlag } from '../useCategoryLLMFlag/useCategoryLLMFlag';
import { useCustomQuery } from '../useCustomQuery';
import { useFeatureFlag } from '../useFeatureFlag';
import { useGeoLocationStorage } from '../useGeoLocationStorage';
import { useLagoon } from '../useLagoon';
import CATEGORY_COUNT from './CategoriesCount.graphql';

export const useCategoryCountSearch = (options) => {
  const [categoryCount, setCategoryCount] = useState({} as any);
  const [queryResult, queryAction] = useCustomQuery(CATEGORY_COUNT, {
    ...options,
    url: config('GRAPHQL_API_URL'),
    accessor: 'getChipCategoriesCount',
    onCompleted: (result) => {
      if (options?.onCompleted) {
        options.onCompleted(result);
      }
      setCategoryCount(result);
    },
  });

  const parsedResult = {
    ...queryResult,
    data: categoryCount?.data || {},
    corelationId: categoryCount?.headers?.[OPTUM_CORRELATION_ID_HEADER] || '',
  };

  return [parsedResult, queryAction];
};
export const getIncludeSpecialityRollupCodes = (
  eachCategory,
  useMedicalNewRollUpCodes,
  useBHNewRollUpCodes,
  useVisionNewRollUpCodes,
  useDentalNewRollUpCodes,
  rollupCodeMap
) => [
  ...new Set(
    eachCategory.includeSpecialityRollupCodes.map((code) => {
      const useNewRollupCode = supportNewCodes({
        coverageType: eachCategory.coverages[0],
        useMedicalNewRollUpCodes,
        useBHNewRollUpCodes,
        useVisionNewRollUpCodes,
        useDentalNewRollUpCodes,
      });
      return useNewRollupCode
        ? code?.trim()
        : rollupCodeMap[code?.trim()] || code?.trim();
    })
  ),
];
const getBehavioralFacilityPrograms = (
  eachCategory,
  bhFpProgramToggleOn,
  bhProgramFacilityAgg
) =>
  eachCategory.label === CareCategories.BEHAVIORAL_HEALTH &&
  bhFpProgramToggleOn &&
  !eachCategory.name
    ? bhProgramFacilityAgg
    : '';

export const useCategoriesCount = (options = {}) => {
  const [enableCategoryCountV2] = useFeatureFlag([
    ConstantsLagoon.FEATURE_FLAGS.ENABLE_CATEGORY_COUNT_V2,
  ]);
  if (enableCategoryCountV2 === true) {
    return useCategoriesCountV2(options);
  }

  // Otherwise, use V1 implementation
  const locale = getLanguage()?.code || 'en';
  const { longitude, latitude, stateCode } = useGeoLocationStorage();

  const currentMember = getCurrentMember();
  const medicalCoverage = getMemberHealthCoverageType(
    currentMember?.eligibility,
    CoverageTypesCodes.MEDICAL
  );

  const { coverageType: coverageTypeFromStore, storeLob } = useChipStore(
    useShallow((state: ChipState) => ({
      coverageType: state.coverageType,
      storeLob: state.lob,
    }))
  );

  const queryString = window.location.search;
  const urlParams = new URLSearchParams(queryString);
  const coverageTypeFromParams = urlParams.get('coverageType');
  const [enableSearchResultsV2] = useFeatureFlag([
    ConstantsLagoon.FEATURE_FLAGS.ENABLE_SEARCH_RESULTS_V2,
  ]);

  const coverageType = enableSearchResultsV2
    ? coverageTypeFromParams || coverageTypeFromStore
    : coverageTypeFromStore;

  const [categoryCount, getCategoryCountDetail] =
    useCategoryCountSearch(options);

  const rollupCodeMapLagoonData = useLagoon(
    Constants.LAGOON_TABLE.ROLLUPCODE_MAPPING
  )();
  const rollupCodeMap = rollupCodeMapLagoonData?.reduce((acc, item) => {
    if (item.source && item.destination) {
      acc[item.source] = item.destination;
    }
    return acc;
  }, {});

  const tierBenefits = useLagoon('tier-benefit')();

  const [
    useMedicalNewRollUpCodes,
    useBHNewRollUpCodes,
    useVisionNewRollUpCodes,
    useDentalNewRollUpCodes,
    uspTierOneFlag,
    enableTier1FromMemberRPK,
    uspToggleFlag,
    bhFpProgramToggleOn,
    eapBHProgramFlag,
    enableClaimsBasedLlmAllLanguages,
  ] = useFeatureFlag([
    ConstantsLagoon.FEATURE_FLAGS.USE_MEDICAL_NEW_ROLLUP_CODES,
    ConstantsLagoon.FEATURE_FLAGS.USE_BH_NEW_ROLLUP_CODES,
    ConstantsLagoon.FEATURE_FLAGS.USE_VISION_NEW_ROLLUP_CODES,
    ConstantsLagoon.FEATURE_FLAGS.USE_DENTAL_NEW_ROLLUP_CODES,
    ConstantsLagoon.FEATURE_FLAGS.USP_TIER1_ENABLE,
    ConstantsLagoon.FEATURE_FLAGS.ENABLE_TIER1_FROM_MEMBER_RPK,
    ConstantsLagoon.FEATURE_FLAGS.USP_ENABLE,
    ConstantsLagoon.FEATURE_FLAGS.ENABLE_BH_FACILITIES_PROGRAM_SEARCH,
    ConstantsLagoon.FEATURE_FLAGS.ENABLE_EAP_FILTER_USING_BH_PROGRAM,
    ConstantsLagoon.FEATURE_FLAGS?.ENABLE_CLAIMS_BASED_LLM_ALL_LANGUAGES,
  ]);

  const searchSuggestionEN = enableClaimsBasedLlmAllLanguages
    ? urlParams.get('searchSuggestionEN')
    : '';
  const featureFlags: [{ key: string; active: boolean }] =
    useLagoon('feature-flags')();

  const memCatergory = currentMember?.membershipCategory || '';
  const membershipCategory = uspToggleFlag ? memCatergory : null;
  const isPreEffective = useCurrentMemberIsPreEffective();
  const currentMemberEffectiveDate = getCoverageEffDateRange(
    currentMember,
    coverageType
  ).startDate;

  const getCategoryCountResults = (passedParams) => {
    const llmFlags = passedParams.payloadwithName.reduce(
      (acc, eachCategory) => {
        acc[eachCategory.label] = {
          practitioner: getCategoryLLMFlag(
            featureFlags,
            'practitioner',
            eachCategory.label
          ),
          facility: getCategoryLLMFlag(
            featureFlags,
            'facility',
            eachCategory.label
          ),
          medical_group: getCategoryLLMFlag(
            featureFlags,
            'medical_group',
            eachCategory.label
          ),
        };
        return acc;
      },
      {}
    );
    getCategoryCountDetail({
      variables: {
        locale,
        providerType: '',
        policyID: currentMember?.policyNumber,
        planYear: getMemberPlanYear(isPreEffective, currentMemberEffectiveDate),
        memberEffectiveDate: isPreEffective ? currentMemberEffectiveDate : null,
        lob: getLob(currentMember?.lineOfBusiness, storeLob),
        population: currentMember?.population,
        membershipCategory,
        rulesPackageKey: getRulesPackageKey(
          currentMember,
          tierBenefits,
          '',
          uspTierOneFlag,
          enableTier1FromMemberRPK
        ),
        latitude,
        longitude,
        stateCode,
        ...(passedParams?.gender && {
          gender: passedParams?.gender,
        }),
        ...(passedParams?.virtualCare && {
          virtualCare: passedParams?.virtualCare,
        }),
        ...(passedParams?.essentialCommunityProvider && {
          essentialCommunityProvider: passedParams?.essentialCommunityProvider,
        }),
        planVariationCode: getPlanVariationCode(currentMember, coverageType),
        claimSystemTypeCode: getClaimOrEligibilitySystemTypeCode(
          currentMember,
          coverageType
        )?.claimSystemTypeCode,
        eligibilitySystemTypeCode: getClaimOrEligibilitySystemTypeCode(
          currentMember,
          coverageType
        )?.eligibilitySystemTypeCode,
        searchRadius: passedParams?.searchRadius,
        autoIncrementRadius: passedParams?.autoIncrementRadius,
        pseudoRollupCode: passedParams?.pseudoRollupCode,
        preferredFacilities: passedParams?.preferredFacilities,
        snpType:
          coverageType == ReverseCoverageTypesCodes.MEDICAL
            ? medicalCoverage?.snpType
            : '',
        searchTerm: searchSuggestionEN || passedParams?.searchTerm,
        isUserTextSearch: passedParams?.isUserTextSearch,
        bhFpProgramToggleOn,
        platinumProgramCode: passedParams?.platinumProgramCode,

        categoryRecords: passedParams.payloadwithName.map((eachCategory) => ({
          ...eachCategory,
          invokeLlmModel_Practitioner:
            llmFlags[eachCategory.label].practitioner,
          invokeLlmModel_Facility: llmFlags[eachCategory.label].facility,
          invokeLlmModel_Medical_Group:
            llmFlags[eachCategory.label].medical_group,
          pcpIndicator:
            passedParams?.useNewAgeAndGenderRollups &&
            eachCategory?.chipCategory === ChipCategories.PRIMARY_CARE
              ? null
              : eachCategory?.pcpIndicator,
          behavioralFacilityPrograms: getBehavioralFacilityPrograms(
            eachCategory,
            bhFpProgramToggleOn,
            /[A-Za-z0-9]/.test(passedParams?.searchTerm)
              ? passedParams.behavioralFacilityProgram
              : null
          ),
          uesType: undefined,
          includeSpecialityRollupCodes: getIncludeSpecialityRollupCodes(
            eachCategory,
            useMedicalNewRollUpCodes,
            useBHNewRollUpCodes,
            useVisionNewRollUpCodes,
            useDentalNewRollUpCodes,
            rollupCodeMap
          ),
          useMedicalNewRollUpCodes,
          useBHNewRollUpCodes,
          useVisionNewRollUpCodes,
          useDentalNewRollUpCodes,
          agesAoeCodes: passedParams.agesAoeCodes,
          aoeCodes: getUpdatedAoeCodesWithEAP(
            eapBHProgramFlag,
            passedParams.preSelectEapFilter,
            eachCategory.aoeCodes,
            passedParams.conditionAoeCodes
          ),
          behavioralProgramId: getBehavioralProgramId(
            eapBHProgramFlag,
            passedParams.preSelectEapFilter
          ),
        })),
      },
    });
  };
  return [categoryCount, getCategoryCountResults];
};
